import { WorkbookRunFooterStatus } from './WorkbookRunFooterStatus';
import { WorkbookContent } from './WorkbookContent';
import { WorkbookHeader } from './WorkbookHeader';
import { WorkbookToolbar } from './WorkbookToolbar';
import { WorkbookNavigation } from './WorkbookNavigation';
import { Activity, memo, useState } from 'react';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Action, Subject } from '@erp/authz';
import type { WorkbookDetail } from '@erp/rpc';
import {
  workbookSourceSchema,
  type WorkbookColumnType,
  type WorkbookSource,
  type WorkbookTable as TableData,
} from '@erp/shared-types';
import { columnReference } from '@erp/workbooks';
import { sourceColumnTypes } from './workbookCatalog';
import { T, useGT } from 'gt-react';
import { useCan } from '@/hooks/useCan';
import { useWorkbookEditor } from './useWorkbookEditor';
import { useWorkbookUploads } from './useWorkbookUploads';
import { useUndoHotkeys } from '@/lib/useUndoTree';
import { moveCaretToLineEdge } from '@/lib/textFieldKeys';
import { newPivotSheet } from './pivot/newPivotSheet';
import { newWorksheetName } from './workbookNames';
import {
  WorkbookSopUpdatesContext,
  useWorkbookSopUpdates,
} from './useWorkbookSopUpdates';
import { WorkbookSopUpdateDialog } from './WorkbookSopUpdateDialog';
import { useWorkbookTabState } from './useWorkbookTabState';
import { useWorkbookWorkspaceAgentTools } from './useWorkbookWorkspaceAgentTools';
import { useWorkbookFollow } from './useWorkbookFollow';
import { WorkbookFollowTurn } from './WorkbookFollow';

/**
 * A tab's content that skips re-rendering while it stays hidden: its props
 * (the editor, above all) change on every edit, and a hidden tab has nothing
 * to show for it. It renders with the latest props once it is shown again.
 */
const TabContent = memo(
  WorkbookContent,
  (previous, next) => !previous.active && !next.active,
);

// Inherited complexity: this change only moves tab state into a hook and passes props (no branches added).
// fallow-ignore-next-line complexity
export function WorkbookWorkspace({ initial }: { initial: WorkbookDetail }) {
  const editor = useWorkbookEditor(initial);
  const gt = useGT();
  // The inset shell already frames the pane; the editor draws its own frame only without it.
  const {
    selection,
    setSelected,
    openedItems,
    setOpened,
    previewItem,
    openItem,
    keepItem,
  } = useWorkbookTabState(initial.id);
  const [busy, setBusy] = useState(false);
  const uploads = useWorkbookUploads(editor, openItem);
  const [canUpdate] = useCan(Action.Update, Subject.Workbook);
  const [canExecute] = useCan(Action.Execute, Subject.Workbook);
  // A Report being laid out takes these keys while it is (see useUndoHotkeys).
  useUndoHotkeys(editor.history, canUpdate && !busy);
  const sopUpdates = useWorkbookSopUpdates(
    editor,
    canUpdate,
    editor.runs.data?.[0]?.id,
  );
  const { definition } = editor;
  const availableIds = new Set([
    'settings',
    'process',
    'runs',
    'automations',
    'versions',
    'add-input',
    'add-worksheet',
    'add-pivot',
    ...definition.files.map((item) => item.id),
    ...definition.sources.map((item) => item.id),
    ...definition.sheets.map((item) => item.id),
    ...definition.outputs.map((item) => item.id),
  ]);
  const opened = openedItems.filter((id) => availableIds.has(id));
  const preview =
    previewItem !== undefined && opened.includes(previewItem)
      ? previewItem
      : undefined;
  const selected =
    selection === '' || availableIds.has(selection)
      ? selection
      : (opened[0] ?? '');
  function closeItem(id: string) {
    if (id === 'process') return;
    const index = opened.indexOf(id);
    const remaining = opened.filter((item) => item !== id);
    setOpened(remaining);
    keepItem(id);
    if (selected === id)
      setSelected(remaining[index] ?? remaining[index - 1] ?? '');
  }
  const latestRun = editor.runs.data?.[0];
  const follow = useWorkbookFollow({
    selection: selected,
    openItem,
    setFollow: editor.setAgentFollow,
  });
  useWorkbookWorkspaceAgentTools({
    definition,
    selected,
    opened,
    openItem: follow.agentOpen,
    sopUpdates,
    canUpdate,
    register: editor.setAgentWorkspace,
  });

  async function act(operation: () => Promise<unknown>) {
    setBusy(true);
    editor.setError(null);
    try {
      await operation();
    } catch (error) {
      editor.setError(
        error instanceof Error
          ? error.message
          : gt('Workbook action failed.', { context: 'workbook action error' }),
      );
    }
    setBusy(false);
  }
  /** Whether the source was added: false when it is invalid. */
  function addSource(next: WorkbookSource): boolean {
    // A schema error's message is a list of issues, not a sentence.
    const validated = workbookSourceSchema.safeParse(next);
    if (!validated.success) {
      editor.setError(
        gt("This data couldn't be added. Check its name and settings.", {
          context: 'workbook invalid source',
        }),
      );
      return false;
    }
    editor.edits.workbook.update((latest) => ({
      ...latest,
      sources: [...latest.sources, validated.data],
    }));
    openItem(validated.data.id, 'add-input');
    return true;
  }
  function addSheet(input: { id: string; name: string }, table: TableData) {
    const id = crypto.randomUUID();
    const source = editor.definition.sources.find(
      (candidate) => candidate.id === input.id,
    );
    // An Entity's columns start with the types the entity gives them.
    const types = source
      ? sourceColumnTypes(source, editor.catalog.data)
      : new Map<string, WorkbookColumnType>();
    editor.edits.workbook.update((latest) => ({
      ...latest,
      sheets: [
        ...latest.sheets,
        {
          id,
          name: newWorksheetName(
            latest,
            input.name,
            gt('sheet', { context: 'workbook new worksheet name suffix' }),
          ),
          folder: '',
          inputId: input.id,
          columns: table.columns.map((name) => ({
            name,
            expression: columnReference(name),
            type: types.get(name) ?? 'text',
          })),
          filters: [],
          lookups: [],
          sort: [],
        },
      ],
    }));
    openItem(id, 'add-worksheet');
  }
  function addPivot(input: { id: string; name: string }) {
    const id = crypto.randomUUID();
    const suffix = gt('pivot', { context: 'workbook new pivot name suffix' });
    editor.edits.workbook.update((latest) => ({
      ...latest,
      sheets: [...latest.sheets, newPivotSheet(latest, input, id, suffix)],
    }));
    openItem(id, 'add-pivot');
  }
  return (
    <WorkbookSopUpdatesContext.Provider value={sopUpdates}>
      <WorkbookFollowTurn trackTurn={follow.trackTurn} selection={selected} />
      <Tabs
        onKeyDown={moveCaretToLineEdge}
        value={selected}
        onValueChange={(id) => openItem(id)}
        // -m-2 swallows the app <main>'s p-2 and the height adds it back: the
        // editor fills the inset frame edge to edge (see SettingsLayout).
        className="-m-2 h-[calc(100%+1rem)] min-h-0 gap-0 overflow-clip"
      >
        <WorkbookHeader editor={editor} canUpdate={canUpdate} busy={busy} />
        <div className="flex min-h-0 flex-1 overflow-clip">
          <WorkbookNavigation
            editor={editor}
            selected={selected}
            opened={opened}
            setSelected={openItem}
            previewItem={(id) => openItem(id, undefined, true)}
            keepItem={keepItem}
            busy={busy}
            canUpdate={canUpdate}
            pendingUploads={uploads.pending}
          />
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <WorkbookToolbar
              editor={editor}
              opened={opened}
              selected={selected}
              preview={preview}
              selectItem={(id) => openItem(id)}
              keepItem={keepItem}
              closeItem={closeItem}
              openSettings={() => openItem('settings')}
              busy={busy}
              canUpdate={canUpdate}
              canExecute={canExecute}
              act={act}
            />
            {editor.error && (
              <div
                role="alert"
                className="border-b bg-destructive/10 p-2 text-sm text-destructive"
              >
                {editor.error}
              </div>
            )}
            {!opened.length && (
              <div className="flex flex-1 items-center justify-center p-3 text-sm text-muted-foreground">
                <T context="workbook no open tabs">
                  Open an item from the sidebar to continue.
                </T>
              </div>
            )}
            {opened.map((id) => (
              <TabsContent
                key={id}
                value={id}
                forceMount
                hidden={id !== selected}
                // Working in a tab keeps it open: a click or a key in it.
                onPointerDownCapture={() => keepItem(id)}
                onKeyDownCapture={() => keepItem(id)}
                className="flex min-h-0 min-w-0 flex-col overflow-auto data-[hidden]:hidden"
              >
                {/* A hidden tab keeps its state but renders nothing while
                    hidden: edits re-render only the tab on screen. */}
                <Activity mode={id === selected ? 'visible' : 'hidden'}>
                  <TabContent
                    editor={editor}
                    selected={id}
                    active={id === selected}
                    busy={busy}
                    canUpdate={canUpdate}
                    canExecute={canExecute}
                    act={act}
                    addSource={addSource}
                    addSheet={addSheet}
                    addPivot={addPivot}
                    openItem={openItem}
                    uploadFiles={uploads.upload}
                  />
                </Activity>
              </TabsContent>
            ))}
          </div>
        </div>
        <footer className="flex shrink-0 items-center gap-3 border-t bg-muted/20 px-3 py-1 text-xs text-muted-foreground">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-1 text-xs text-muted-foreground"
            onClick={() => openItem('versions')}
            aria-label={gt('Saved versions', {
              context: 'workbook versions nav',
            })}
          >
            <T context="workbook footer versions">Saved versions</T>
          </Button>
          <span role="status">
            {editor.saving
              ? gt('Saving…', { context: 'workbook save state' })
              : editor.dirty
                ? gt('Unsaved changes', { context: 'workbook save state' })
                : gt('Autosaved', { context: 'workbook save state' })}
          </span>
          {latestRun && (
            <WorkbookRunFooterStatus run={latestRun} definition={definition} />
          )}
        </footer>
        <WorkbookSopUpdateDialog definition={definition} />
      </Tabs>
    </WorkbookSopUpdatesContext.Provider>
  );
}
